import React, { type FC, type KeyboardEvent, useState } from "react";
import { useTranslation } from "@edreamsodigeo/frontend-core";
import {
  Field,
  InnerLabel,
  Input,
  ValidationErrorMessage,
} from "@edreamsodigeo/prisma-design-system";

import { KeyCodes } from "../../../../utils";
import type { PasswordInputProps } from "./contract";
import usePasswordInputField from "./hook/usePasswordInputField";
import InputPasswordIcon from "./InputIcon";
import { PasswordErrorsMessageBody } from "./PasswordErrorsMessageBody";

export const PasswordInput: FC<PasswordInputProps> = ({
  id = "id-password-input",
  password,
  setPassword,
  errors,
  clearErrors,
  disabled,
  onEnter,
  onClick,
  trackingPrefix,
}) => {
  const { t } = useTranslation();
  const [inputFocus, setInputFocus] = useState(false);

  const hasErrors = !!errors.length;

  const {
    fieldProps,
    labelProps,
    inputProps,
    iconProps,
    onClickIconVisibility,
  } = usePasswordInputField({
    id,
    value: password,
    type: "password",
    onChange({ currentTarget }) {
      setPassword(currentTarget.value);
      if (hasErrors) {
        clearErrors();
      }
    },
    onFocus: () => setInputFocus(true),
    onBlur: () => setInputFocus(false),
    validate: () => !hasErrors,
  });

  const onKeyDownHandler = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (KeyCodes.Enter(event)) {
      onEnter?.();
    }
  };

  return (
    <>
      <Field {...fieldProps}>
        <InnerLabel {...labelProps}>{t("auth.form.password.label")}</InnerLabel>
        <Input
          {...inputProps}
          invalid={!!hasErrors}
          disabled={disabled}
          onKeyDown={onKeyDownHandler}
          onClick={onClick}
        />
        <InputPasswordIcon
          {...iconProps}
          type={inputProps.type}
          invalid={!!hasErrors}
          focus={inputFocus}
          onClick={onClickIconVisibility}
        />
      </Field>
      <ValidationErrorMessage htmlFor={id} invalid={!!hasErrors}>
        {(errors || []).map((err, i) => {
          return (
            <PasswordErrorsMessageBody
              key={i}
              error={err}
              trackingPrefix={trackingPrefix}
            >
              {t(err)}
            </PasswordErrorsMessageBody>
          );
        })}
      </ValidationErrorMessage>
    </>
  );
};
